import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import {
  fetchProjects,
  createProject,
  updateProjectData,
  deleteProjectById,
} from "../../../store/features/project/projectSlice";
import { Button, Table, Spinner, ProgressBar, Badge } from "react-bootstrap";
import { toast } from "react-toastify";
import { BsPencilSquare, BsTrash } from "react-icons/bs";
import { TbPackages } from "react-icons/tb";
import { MdHistory } from "react-icons/md";
import { IoDocumentTextOutline } from "react-icons/io5";

import ProjectModal from "./components/modals/ProjectModal";
import FilterData from "./components/FilterData";
import ProjectHistory from "./components/modals/ProjectHistory";
import ReactPaginate from "react-paginate";
import NoDataFound from "../../../components/NoDataFound";
import { FaFolderOpen } from "react-icons/fa";
import { STATUS_OPTIONS } from "../../../utils/filterOptions";
import { filterBySearchAndStatus } from "../../../utils/filterUtils";

// Debounce hook
function useDebounce(value, delay = 400) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const ProjectSchema = Yup.object({
  projectName: Yup.string().required(),
  description: Yup.string().required(),
  cost: Yup.number().required(),
  budget: Yup.number().required(),
  assistantSuperAdminId: Yup.string().required(),
});

function Projects({
  showForm,
  setShowForm,
  setProjectInfo,
  setTabKey,
  setWpInfo,
  editingProject,
  setEditingProject,
}) {
  const dispatch = useDispatch();

  const { assistantSuperAdmin } = useSelector((state) => state.users);
  const { workPackages } = useSelector((state) => state.workPackages);
  const { tasks } = useSelector((state) => state.tasks);
  const { projects, loading, paginationObj } = useSelector(
    (state) => state.projects
  );
  const { role } = useSelector((state) => state.auth);

  const [currentRole, setCurrentRole] = useState("");
  const [showProjectHistory, setShowProjectHistory] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const limit = 10;

  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 400);

  // Fetch projects on mount or filters/sorting change (without filtering by status or search here)
  useEffect(() => {
    dispatch(
      fetchProjects({
        page: currentPage + 1,
        limit,
        sortBy,
        sortOrder,
        assistantSuperAdminId: currentRole || undefined,
      })
    );
  }, [currentPage, sortBy, sortOrder, currentRole, dispatch, limit]);

  useEffect(() => {
    if (paginationObj?.totalPages) {
      setPageCount(paginationObj.totalPages);
    }
  }, [paginationObj]);

  // Whenever filter/search/sort/role changes, reset to first page
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, statusFilter, sortBy, sortOrder, currentRole]);

  // Frontend filtering for status and search

  const getProgressLevelStatus = (progress = 0) => {
    if (progress === 0) return "Pending";
    if (progress < 50) return "At Risk";
    if (progress < 100) return "On Track";
    return "Completed";
  };
  const filteredProjects = filterBySearchAndStatus({
    data: projects,
    searchTerm: debouncedSearchTerm,
    statusFilter,
    searchKeys: ["projectName", "description"],
  });

  const getProgressBadgeVariant = (progress = 0) => {
    if (progress === 0) return "secondary";
    if (progress < 50) return "danger";
    if (progress < 100) return "info";
    return "success";
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await dispatch(deleteProjectById(id)).unwrap();
      toast.info("Project deleted");
      dispatch(
        fetchProjects({
          page: currentPage + 1,
          limit,
          sortBy,
          sortOrder,
          assistantSuperAdminId: currentRole || undefined,
        })
      );
      // Optionally refetch or update projects here if needed
    } catch (err) {
      toast.error("Error deleting Project");
    }
  };

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setCurrentPage(0);
  };

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleRoleChange = useCallback((value) => {
    setCurrentRole(value);
  }, []);

  return (
    <>
      {loading ? (
        <Spinner animation="border" />
      ) : (
        <>
          {projects?.length > 0 ? (
            <div className="table-wrapper">
              <FilterData
                searchValue={searchTerm}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Search projects..."
                selectConfigs={[
                  {
                    label: "All Status",
                    value: statusFilter,
                    onChange: setStatusFilter,
                    options: STATUS_OPTIONS,
                  },
                ]}
              />
              <Table responsive>
                <thead>
                  <tr>
                    <th onClick={() => handleSort("projectName")}>
                      Project Name{" "}
                      {sortBy === "projectName" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("description")}>
                      Description{" "}
                      {sortBy === "description" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th>Assistant Superadmin</th>
                    <th onClick={() => handleSort("cost")}>
                      Cost{" "}
                      {sortBy === "cost" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("budget")}>
                      Budget{" "}
                      {sortBy === "budget" && (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th>Progress Level</th>
                    <th>Progress Status</th>
                    <th onClick={() => handleSort("startDate")}>
                      Start Date{" "}
                      {sortBy === "startDate" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th onClick={() => handleSort("endDate")}>
                      End Date{" "}
                      {sortBy === "endDate" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </th>
                    <th>Document</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects?.map((project) => (
                    <tr key={project._id}>
                      <td>
                        <span
                          className="link-opacity-100 text-primary cursor-pointer"
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setProjectInfo(project);
                            setTabKey("workPackages");
                            setWpInfo({});
                          }}
                        >
                          {project.projectName}
                        </span>
                        <p
                          style={{
                            fontSize: 12,
                            color: "#666",
                            fontWeight: 600,
                          }}
                        >
                          <small>
                            {project.workpackageCount || 0}
                            WPs,
                          </small>{" "}
                          <small>
                            {project.taskCount || 0}
                            Tasks
                          </small>
                        </p>
                      </td>
                      <td>
                        {project.description?.length > 50 ? (
                          <>
                            {expandedDescriptions[project._id]
                              ? project.description
                              : `${project.description.slice(0, 50)}... `}
                            <span
                              onClick={() => toggleDescription(project._id)}
                              style={{ color: "blue", cursor: "pointer" }}
                            >
                              {expandedDescriptions[project._id]
                                ? "Show less"
                                : "Show more"}
                            </span>
                          </>
                        ) : (
                          project.description
                        )}
                      </td>
                      <td>
                        {
                          assistantSuperAdmin?.find(
                            (pm) => pm._id === project.assistantSuperAdminId
                          )?.name
                        }
                      </td>
                      <td>{project.cost}</td>
                      <td>{project.budget}</td>
                      <td className="text-center">
                        <div className="progessbar-wrapper">
                          <div className="progressbar-tooltip">{`${
                            project.progressLevel || 0
                          }%`}</div>
                          <ProgressBar now={project.progressLevel || 0} />
                        </div>
                      </td>
                      <td>
                        <Badge
                          className="mt-1"
                          bg={getProgressBadgeVariant(project.progressLevel)}
                        >
                          {getProgressLevelStatus(project.progressLevel)}
                        </Badge>
                      </td>
                      <td>
                        {project.startDate
                          ? format(new Date(project.startDate), "MM/dd/yyyy")
                          : "-"}
                      </td>
                      <td>
                        {project.endDate
                          ? format(new Date(project.endDate), "MM/dd/yyyy")
                          : "-"}
                      </td>
                      <td style={{ minWidth: 120 }}>
                        {Array.isArray(project.document) &&
                          project.document.length > 0 && (
                            <div className="d-flex flex-wrap">
                              {project.document.map((doc, idx) => (
                                <Button
                                  key={idx}
                                  variant="light"
                                  className="me-1"
                                >
                                  <a
                                    href={doc}
                                    target="_blank"
                                    rel="noreferrer"
                                    download
                                  >
                                    <IoDocumentTextOutline size="1.2rem" />
                                  </a>
                                </Button>
                              ))}
                            </div>
                          )}
                      </td>
                      <td className="actions">
                        <Button
                          title="Work Packages"
                          size="sm"
                          onClick={() => {
                            setProjectInfo(project);
                            setTabKey("workPackages");
                            setWpInfo({});
                          }}
                          className="me-1"
                        >
                          <TbPackages />
                        </Button>

                        <Button
                          title="History"
                          size="sm"
                          onClick={() => {
                            setSelectedProject(project);
                            setShowProjectHistory(true);
                          }}
                          className="me-1"
                        >
                          <MdHistory />
                        </Button>

                        {role !== "auditor" && (
                          <>
                            <Button
                              title="Edit"
                              size="sm"
                              onClick={() => handleEdit(project)}
                              className="me-1"
                            >
                              <BsPencilSquare />
                            </Button>

                            <Button
                              title="Delete"
                              size="sm"
                              variant="danger"
                              onClick={() => handleDelete(project._id)}
                            >
                              <BsTrash />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              {paginationObj?.totalPages > 1 && (
                <ReactPaginate
                  breakLabel="..."
                  nextLabel="next >"
                  previousLabel="< prev"
                  onPageChange={handlePageClick}
                  pageCount={pageCount}
                  forcePage={currentPage}
                  containerClassName="pagination"
                  activeClassName="active"
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={1}
                />
              )}
            </div>
          ) : (
            <NoDataFound
              icon={<FaFolderOpen size={50} />}
              title="Projects not found"
              description="Try to add some projects."
            />
          )}

          {showForm && (
            <ProjectModal
              showForm={showForm}
              setShowForm={setShowForm}
              editingProject={editingProject}
              setEditingProject={setEditingProject}
            />
          )}

          {showProjectHistory && selectedProject && (
            <ProjectHistory
              showProjectHistory={showProjectHistory}
              setShowProjectHistory={setShowProjectHistory}
              selectedProject={selectedProject}
            />
          )}
        </>
      )}
    </>
  );
}

export default Projects;
